<!-- eslint-disable vuejs-accessibility/label-has-for -->
<script setup>
import { Orientation } from '@src/store/dicom-web/classification-typing';
import { seriesClassification } from '@src/store/dicom-web/SeriesClassification';
import { computed, ref, watch } from 'vue';

import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';

import GlassDropdown from '../GlassDropdown.vue';
import PolarmapSectors from './PolarmapSectors.vue';

const props = defineProps({
  viewerID: { type: String, default: null },
  insertionPoint: { type: Number, default: -18 },
  badColor: { type: String, default: '#DA3459' },
  goodColor: { type: String, default: '#2da0ffbe' },
});

const colormapModeOptions = [
  { value: 'raw', label: 'Raw' },
  { value: 'z_score', label: 'Z-Score' },
];

const orientationOptions = [
  { value: Orientation.Sax, label: 'SAX' },
  { value: Orientation.Lax2CV, label: 'LAX' },
];

// TODO: Move this to a separate config file or fetch from backend
const biomarkers = {
  ocgDiaMaxIntensity: {
    muHealthy: 1.15629,
    sigmaHealthy: 0.173803,
    clipN: 2,
    zDiff: -0.45,
    clinicalLabel: 'B-MORE diastole',
  },
  ocgEntropy: {
    muHealthy: 4.3825,
    sigmaHealthy: 0.192653,
    clipN: 2,
    zDiff: -0.45,
    clinicalLabel: 'B-MORE micro',
  },
};

const colormapMode = ref('raw');
const colormap = ref('plasma');
const interpolationMode = ref('none');
const orientation = ref(Orientation.Sax);
const showTerritories = ref(true);
const canToggleTerritories = computed(
  () => orientation.value === Orientation.Sax,
);

const biomarkerEntries = computed(() => Object.entries(biomarkers));

const releaseKeyboardLock = (event) => {
  event?.target?.blur?.();
};

const viewerIndx = computed(() => {
  const viewportStateStore = useViewportStateStore();
  return viewportStateStore.getSelectedImageIndices(props.viewerID);
});

const updateOrientationFromClassification = () => {
  if (!props.viewerID) return;
  const classification = seriesClassification.getClassificationByViewport(
    props.viewerID,
  );
  if (classification && classification.orientation) {
    orientation.value = classification.orientation;
  }
};

updateOrientationFromClassification();

watch(
  () => ({
    seriesIdx: viewerIndx.value.seriesIdx,
    sliceIdx: viewerIndx.value.sliceIdx,
  }),
  () => {
    updateOrientationFromClassification();
  },
);

watch(orientation, (newOrientation) => {
  if (newOrientation !== Orientation.Sax) {
    showTerritories.value = false;
  }
});
</script>

<template>
  <div class="controls-container">
    <GlassDropdown
      v-model="colormapMode"
      label="Colorbar Mode:"
      :options="colormapModeOptions"
      @change="releaseKeyboardLock"
    />

    <GlassDropdown
      v-model="orientation"
      label="Orientation:"
      :options="orientationOptions"
      @change="releaseKeyboardLock"
    />

    <div v-if="canToggleTerritories" class="control-group">
      <label for="show-territories">Show Territories:</label>
      <input
        id="show-territories"
        v-model="showTerritories"
        type="checkbox"
        class="control-checkbox"
        @change="releaseKeyboardLock"
      >
    </div>
  </div>
  <div class="polarmap-carousel-shell">
    <div class="polarmap-manager">
      <div
        v-for="[key, biomarker] in biomarkerEntries"
        :key="key"
        class="polarmap-display"
      >
        <PolarmapSectors
          :insertion-point="insertionPoint"
          :colormap="colormap"
          :interpolation-mode="interpolationMode"
          :colormap-mode="colormapMode"
          :mu-healthy="biomarker.muHealthy"
          :sigma-healthy="biomarker.sigmaHealthy"
          :clip-n="biomarker.clipN"
          :z-diff="biomarker.zDiff"
          :bad-color="badColor"
          :good-color="goodColor"
          :viewer-i-d="viewerID"
          :biomarker-target="key"
          :clinical-label="biomarker.clinicalLabel"
          :show-territories="showTerritories"
          :orientation="orientation"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.polarmap-carousel-shell {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.polarmap-manager {
  display: flex;
  gap: 16px;
  padding: 2px 2px 8px;
  scrollbar-width: none;
}

.polarmap-manager::-webkit-scrollbar {
  display: none;
}

.controls-container {
  display: flex;
  gap: 12px;
  padding: 10px;
  border-radius: 6px;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 15px;
}

.control-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.control-group:focus-within {
  border-radius: 6px;
}

.control-group label {
  color: #b1b2bf;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}

.control-checkbox {
  width: 14px;
  height: 14px;
  accent-color: #6aa5ff;
  cursor: pointer;
}

.polarmap-display {
  height: 300px;
  width: 320px;
  scroll-snap-align: start;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
}
</style>
